import React, { useState, useEffect } from 'react';
import AdUnit from '../components/AdUnit';
import SEO from '../components/SEO';

const PasswordGeneratorPage = () => {
  const [password, setPassword] = useState('');
  const [passwords, setPasswords] = useState([]);
  const [length, setLength] = useState(16);
  const [quantity, setQuantity] = useState(1);
  const [includeUppercase, setIncludeUppercase] = useState(true);
  const [includeLowercase, setIncludeLowercase] = useState(true);
  const [includeNumbers, setIncludeNumbers] = useState(true);
  const [includeSymbols, setIncludeSymbols] = useState(true);
  const [excludeSimilar, setExcludeSimilar] = useState(false);
  const [excludeAmbiguous, setExcludeAmbiguous] = useState(false);
  const [customSymbols, setCustomSymbols] = useState('!@#$%^&*()_+-=[]{}|;:,.<>?');
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [copiedAll, setCopiedAll] = useState(false);
  const [strength, setStrength] = useState({ score: 0, label: '', color: '' });

  // Character sets
  const charSets = {
    uppercase: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ',
    lowercase: 'abcdefghijklmnopqrstuvwxyz',
    numbers: '0123456789',
    similar: 'il1Lo0O',
    ambiguous: '{}[]()/\\\'"`~,;:.<>'
  };

  // Generate character pool based on options
  const getCharacterPool = () => {
    let pool = '';
    
    if (includeUppercase) pool += charSets.uppercase;
    if (includeLowercase) pool += charSets.lowercase;
    if (includeNumbers) pool += charSets.numbers;
    if (includeSymbols) pool += customSymbols;
    
    if (excludeSimilar) {
      pool = pool.split('').filter(c => !charSets.similar.includes(c)).join('');
    }
    
    if (excludeAmbiguous) {
      pool = pool.split('').filter(c => !charSets.ambiguous.includes(c)).join('');
    }
    
    return pool;
  };

  // Generate a single password
  const generatePassword = () => {
    const pool = getCharacterPool();
    if (pool.length === 0) return '';
    
    const array = new Uint32Array(length);
    crypto.getRandomValues(array);
    
    let result = '';
    for (let i = 0; i < length; i++) {
      result += pool[array[i] % pool.length];
    }
    
    return result;
  };

  // Generate multiple passwords
  const generatePasswords = () => {
    const pool = getCharacterPool();
    if (pool.length === 0) {
      alert('Please select at least one character type');
      return;
    }
    
    const newPasswords = Array.from({ length: quantity }, () => generatePassword());
    setPasswords(newPasswords);
    if (newPasswords.length > 0) {
      setPassword(newPasswords[0]);
    }
  };

  // Calculate password strength
  const calculateStrength = (pwd) => {
    if (!pwd) return { score: 0, label: 'None', color: '#e0e0e0' };
    
    let score = 0;
    
    // Length scoring
    if (pwd.length >= 8) score += 1;
    if (pwd.length >= 12) score += 1;
    if (pwd.length >= 16) score += 1;
    if (pwd.length >= 20) score += 1;
    
    // Character variety scoring
    if (/[a-z]/.test(pwd)) score += 1;
    if (/[A-Z]/.test(pwd)) score += 1;
    if (/[0-9]/.test(pwd)) score += 1;
    if (/[^a-zA-Z0-9]/.test(pwd)) score += 1;
    
    // Entropy bonus
    const uniqueChars = new Set(pwd).size;
    if (uniqueChars >= pwd.length * 0.7) score += 1;
    
    // Determine strength label
    let label, color;
    if (score <= 2) {
      label = 'Very Weak';
      color = '#f44336';
    } else if (score <= 4) {
      label = 'Weak';
      color = '#ff9800';
    } else if (score <= 6) {
      label = 'Fair';
      color = '#ffeb3b';
    } else if (score <= 8) {
      label = 'Strong';
      color = '#8bc34a';
    } else {
      label = 'Very Strong';
      color = '#4caf50';
    }
    
    return { score: Math.min(score, 9), label, color };
  };

  // Update strength when password changes
  useEffect(() => {
    setStrength(calculateStrength(password));
  }, [password]);

  // Auto-generate on mount
  useEffect(() => {
    generatePasswords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Copy single password
  const copyToClipboard = async (pwd, index) => {
    try {
      await navigator.clipboard.writeText(pwd);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  // Copy all passwords
  const copyAllToClipboard = async () => {
    if (passwords.length === 0) return;
    try {
      await navigator.clipboard.writeText(passwords.join('\n'));
      setCopiedAll(true);
      setTimeout(() => setCopiedAll(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  // Preset configurations
  const presets = [
    { label: 'Simple (8 chars)', length: 8, upper: true, lower: true, nums: false, syms: false },
    { label: 'Medium (12 chars)', length: 12, upper: true, lower: true, nums: true, syms: false },
    { label: 'Strong (16 chars)', length: 16, upper: true, lower: true, nums: true, syms: true },
    { label: 'Very Strong (24 chars)', length: 24, upper: true, lower: true, nums: true, syms: true },
    { label: 'PIN (4 digits)', length: 4, upper: false, lower: false, nums: true, syms: false },
    { label: 'PIN (6 digits)', length: 6, upper: false, lower: false, nums: true, syms: false }
  ];

  const applyPreset = (preset) => {
    setLength(preset.length);
    setIncludeUppercase(preset.upper);
    setIncludeLowercase(preset.lower);
    setIncludeNumbers(preset.nums);
    setIncludeSymbols(preset.syms);
  };

  return (
    <div>
      <SEO 
        title="Password Generator - Create Strong Secure Passwords" 
        description="Free online password generator. Create strong, secure passwords with customizable length, character types, and options. Generate multiple passwords at once."
        keywords="password generator, secure password, random password, strong password, password creator, online password generator, bulk password"
      />
      
      <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px' }}>
        <h1 style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>Password Generator</h1>
        <p style={{ marginBottom: '20px', color: '#666' }}>
          Generate strong, secure passwords with customizable options. All passwords are generated locally in your browser.
        </p>
        
        <AdUnit />

        {/* Main Password Display */}
        <div style={{ marginTop: '20px', marginBottom: '30px' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '15px',
              padding: '20px',
              backgroundColor: '#f5f5f5',
              borderRadius: '8px',
              border: '2px solid #e0e0e0'
            }}
          >
            <input
              type="text"
              value={password}
              readOnly
              style={{
                flex: 1,
                fontSize: '24px',
                fontFamily: 'monospace',
                padding: '10px',
                border: 'none',
                backgroundColor: 'transparent',
                outline: 'none',
                letterSpacing: '2px'
              }}
            />
            <button
              onClick={() => copyToClipboard(password, -1)}
              style={{
                padding: '12px 24px',
                backgroundColor: copiedIndex === -1 ? '#4caf50' : '#2196f3',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '500',
                transition: 'all 0.2s ease'
              }}
            >
              {copiedIndex === -1 ? '✓ Copied!' : '📋 Copy'}
            </button>
            <button
              onClick={generatePasswords}
              style={{
                padding: '12px 24px',
                backgroundColor: '#4caf50',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '500'
              }}
            >
              🔄 Generate
            </button>
          </div>
          
          {/* Strength Indicator */}
          <div style={{ marginTop: '15px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
              <span style={{ fontSize: '14px', color: '#666' }}>Password Strength</span>
              <span style={{ fontSize: '14px', fontWeight: '500', color: strength.color }}>{strength.label}</span>
            </div>
            <div style={{ height: '8px', backgroundColor: '#e0e0e0', borderRadius: '4px', overflow: 'hidden' }}>
              <div
                style={{
                  height: '100%',
                  width: `${(strength.score / 9) * 100}%`,
                  backgroundColor: strength.color,
                  transition: 'all 0.3s ease',
                  borderRadius: '4px'
                }}
              />
            </div>
          </div>
        </div>

        <div className="main-content" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px' }}>
          {/* Settings Section */}
          <div className="settings-section">
            <h2 style={{ fontSize: '1.3rem', marginBottom: '15px' }}>Settings</h2>
            
            {/* Presets */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Quick Presets:
              </label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {presets.map((preset, index) => (
                  <button
                    key={index}
                    onClick={() => applyPreset(preset)}
                    style={{
                      padding: '6px 12px',
                      border: '1px solid #ddd',
                      borderRadius: '16px',
                      backgroundColor: 'white',
                      color: '#333',
                      cursor: 'pointer',
                      fontSize: '12px',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f0f0'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Length */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Password Length: {length}
              </label>
              <input
                type="range"
                min="4"
                max="64"
                value={length}
                onChange={(e) => setLength(parseInt(e.target.value))}
                style={{ width: '100%' }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#666' }}>
                <span>4</span>
                <span>64</span>
              </div>
            </div>

            {/* Quantity */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Quantity: {quantity}
              </label>
              <input
                type="range"
                min="1"
                max="50"
                value={quantity}
                onChange={(e) => setQuantity(parseInt(e.target.value))}
                style={{ width: '100%' }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#666' }}>
                <span>1</span>
                <span>50</span>
              </div>
            </div>

            {/* Character Types */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '12px', fontWeight: '500' }}>
                Character Types:
              </label>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={includeUppercase}
                    onChange={(e) => setIncludeUppercase(e.target.checked)}
                    style={{ marginRight: '10px', width: '18px', height: '18px' }}
                  />
                  <span style={{ fontSize: '14px' }}>Uppercase (A-Z)</span>
                </label>
                
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={includeLowercase}
                    onChange={(e) => setIncludeLowercase(e.target.checked)}
                    style={{ marginRight: '10px', width: '18px', height: '18px' }}
                  />
                  <span style={{ fontSize: '14px' }}>Lowercase (a-z)</span>
                </label>
                
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={includeNumbers}
                    onChange={(e) => setIncludeNumbers(e.target.checked)}
                    style={{ marginRight: '10px', width: '18px', height: '18px' }}
                  />
                  <span style={{ fontSize: '14px' }}>Numbers (0-9)</span>
                </label>
                
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={includeSymbols}
                    onChange={(e) => setIncludeSymbols(e.target.checked)}
                    style={{ marginRight: '10px', width: '18px', height: '18px' }}
                  />
                  <span style={{ fontSize: '14px' }}>Symbols (!@#$...)</span>
                </label>
              </div>
            </div>

            {/* Custom Symbols */}
            {includeSymbols && (
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  Custom Symbols:
                </label>
                <input
                  type="text"
                  value={customSymbols}
                  onChange={(e) => setCustomSymbols(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontFamily: 'monospace',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
            )}

            {/* Exclusions */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '12px', fontWeight: '500' }}>
                Exclusions:
              </label>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={excludeSimilar}
                    onChange={(e) => setExcludeSimilar(e.target.checked)}
                    style={{ marginRight: '10px', width: '18px', height: '18px' }}
                  />
                  <span style={{ fontSize: '14px' }}>Exclude similar characters (i, l, 1, L, o, 0, O)</span>
                </label>
                
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={excludeAmbiguous}
                    onChange={(e) => setExcludeAmbiguous(e.target.checked)}
                    style={{ marginRight: '10px', width: '18px', height: '18px' }}
                  />
                  <span style={{ fontSize: '14px' }}>Exclude ambiguous characters {'{ } [ ] ( ) / \\ \' " ` ~'}</span>
                </label>
              </div>
            </div>
          </div>

          {/* Generated Passwords List */}
          <div className="output-section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
              <h2 style={{ fontSize: '1.3rem', margin: 0 }}>Generated Passwords ({passwords.length})</h2>
              {passwords.length > 1 && (
                <button
                  onClick={copyAllToClipboard}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: copiedAll ? '#4caf50' : '#e0e0e0',
                    color: copiedAll ? 'white' : '#333',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '500',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {copiedAll ? '✓ Copied All' : '📋 Copy All'}
                </button>
              )}
            </div>
            
            <div
              style={{
                border: '1px solid #ddd',
                borderRadius: '8px',
                padding: '15px',
                backgroundColor: '#fafafa',
                maxHeight: '500px',
                overflowY: 'auto'
              }}
            >
              {passwords.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#666', padding: '40px' }}>
                  <div style={{ fontSize: '48px', marginBottom: '15px', opacity: 0.3 }}>🔐</div>
                  <div>Click "Generate" to create passwords</div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {passwords.map((pwd, index) => (
                    <div
                      key={index}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '10px 12px',
                        backgroundColor: index === 0 ? '#e3f2fd' : 'white',
                        border: `1px solid ${index === 0 ? '#2196f3' : '#e0e0e0'}`,
                        borderRadius: '4px'
                      }}
                    >
                      <code style={{ fontFamily: 'monospace', fontSize: '14px', color: '#333', letterSpacing: '1px' }}>
                        {pwd}
                      </code>
                      <button
                        onClick={() => copyToClipboard(pwd, index)}
                        style={{
                          padding: '4px 10px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          backgroundColor: copiedIndex === index ? '#4caf50' : 'white',
                          color: copiedIndex === index ? 'white' : '#666',
                          cursor: 'pointer',
                          fontSize: '12px',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        {copiedIndex === index ? '✓' : '📋'}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Information Section */}
        <div className="info-section" style={{ marginTop: '40px', borderTop: '1px solid #eee', paddingTop: '20px' }}>
          <h2 style={{ fontSize: '1.5rem', marginBottom: '15px' }}>Password Security Tips</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
            <div>
              <h3 style={{ fontSize: '1.1rem', marginBottom: '10px', color: '#333' }}>Strong Password Guidelines</h3>
              <p style={{ fontSize: '14px', lineHeight: '1.6', color: '#666' }}>
                Use at least 12 characters. Mix uppercase, lowercase, numbers, and symbols.
                Avoid common words, personal information, and sequential patterns (123, abc).
              </p>
            </div>
            
            <div>
              <h3 style={{ fontSize: '1.1rem', marginBottom: '10px', color: '#333' }}>Use a Password Manager</h3>
              <p style={{ fontSize: '14px', lineHeight: '1.6', color: '#666' }}>
                Don't try to memorize complex passwords. Use a reputable password manager to store
                and auto-fill your credentials securely.
              </p>
            </div>
            
            <div>
              <h3 style={{ fontSize: '1.1rem', marginBottom: '10px', color: '#333' }}>Unique Passwords</h3>
              <p style={{ fontSize: '14px', lineHeight: '1.6', color: '#666' }}>
                Never reuse passwords across different accounts. If one site is compromised, 
                all your accounts with the same password become vulnerable.
              </p>
            </div>
          </div>
        </div>
      </div>
      
      <style jsx>{`
        @media (max-width: 768px) {
          .container {
            padding: 15px;
          }
          
          .main-content {
            grid-template-columns: 1fr !important;
            gap: 20px;
          }
        }
      `}</style>
    </div>
  );
};

export default PasswordGeneratorPage;

